import EwaybillType from "@/enum/EwaybillType";
import LogisticsCompany from "@/enum/LogisticsCompany";
import LogisticsCompanyType from "@/enum/LogisticsCompanyType";
import { get, post, postJson } from "@/services/http/backend";

export interface EwaybillAccount {
  companyId: number;

  id: number;

  username: string;

  platformUserId: string;

  platformUsername: string;

  subscriptions: EwaybillSubscription[];

  /**
   * 电子账号平台类型
   */
  type: EwaybillType;

  validationToken: string;

  /** 展示电子面单账号的时候，优先展示非空的 name 字段,其次是 platformUsername */
  name?: string;

  isDeleted: boolean;
}

export interface EwaybillSubscription {
  id: number;
  branchAccounts: EwaybillBranchAccount[];
  ewaybillSubscriptionId: number;
  logisticsCompany: LogisticsCompany;
  logisticsCompanyType: LogisticsCompanyType;
  supportsChildWaybillNoType: boolean;
}

export interface EwaybillBranchAccount {
  id: number;
  branchCode: string;
  branchName: string;
  ewaybillBranchAccountId: number;
  serviceOptionsJson: string;
  shippingAddresses: EwaybillShippingAddress[];
  waybillNoTypeCode: string;
  waybillNoTypeName: string;

  allocatedQuantity: number;

  cancelQuantity: number;

  quantity: number;

  needIgnoreWhenGetShareCode: boolean;
}

export interface EwaybillShippingAddress {
  id: number;
  city: string;
  district: string;
  province: string;
  street: string;
  town: string;
}

interface EwaybillAccountListWithExpiredInfoItem {
  ewaybillAccount: EwaybillAccount;
  /**电子面单账号授权是否已过期 */
  hasExpired: boolean;

  /**电子面单账号授权过期时间戳 */
  tokenExpiringTimeMillis: number;

  /**电子面单账号授权是否将要过期 */
  willExpired: boolean;
}

export interface EwaybillAccountListWithExpiredInfo {
  items: EwaybillAccountListWithExpiredInfoItem[];

  /**授权已过期过期的电子面单账号总数 */
  totalHasExpiredCount: number;

  /**授权将要过期过期的电子面单账号总数 */
  totalWillExpiredCount: number;
}

/**
 * 查询当前账号电子面单账号列表 (带授权过期信息)
 */
export function getEwaybillAccountListWithExpiredInfo() {
  return get<EwaybillAccountListWithExpiredInfo>("/v2/ewaybill_account/list");
}

/**
 * 查询当前账号共享提供者视角下的电子面单账号列表
 */
export function getProviderViewEwaybillAccountList() {
  return get<EwaybillAccount[]>("/v2/ewaybill_account/provider_view/list");
}

/**
 * 查询当前账号共享提供者视角下的电子面单账号列表
 */
export function getConsumerViewEwaybillAccountList() {
  return get<EwaybillAccount[]>("/v2/ewaybill_account/consumer_view/list");
}

interface CreateOfficialEwaybillAccountParams {
  branchCode?: string;
  branchName?: string;
  extraInfoJson?: string;
  logisticsCompany?: LogisticsCompany;
  password: string;
  username: string;
}

/**
 * 添加快递官方网点信息
 */
export function createOfficialEwaybillAccountV2(params: CreateOfficialEwaybillAccountParams) {
  return postJson("/v2/ewaybill_account/create_official_ewaybill_account", params);
}

/**
 * 客户编码有效性校验
 * @deprecated
 */
export function checkCustomerCode(customerCode) {
  return get("/ewaybill_account/jd_express/customer_code/check", { customerCode });
}

/**
 * 增加京东物流客户编码
 * @deprecated
 */
export function addCustomerCode(customerCode) {
  return postJson("/ewaybill_account/jd_express/customer_code/add", { customerCode });
}

/**
 * 判断同步电子面单是否成功
 * @deprecated
 */
export function syncEwaybillAccount() {
  return postJson<SyncEwaybillAccountResult>("/ewaybill_account/sync");
}

/**
 * 各个电子面单账号同步结果项
 */
export interface EwaybillAccountSyncResultItem {
  /**电子面单账号同步失败错误信息（同步成功时该字段为空） */
  errorMessage: string;

  /**电子面单账号 ID */
  ewaybillAccountId: number;

  /**电子面单账号类型 */
  ewaybillType: EwaybillType;

  /**电子面单账号平台用户名 */
  platformUsername: string;

  /**电子面单账号是否同步成功 */
  success: boolean;
}

export interface SyncEwaybillAccountResult {
  allSuccess?: boolean;

  items: EwaybillAccountSyncResultItem[];
}

/**
 * 同步电子面单账号
 */
export function syncEwaybillAccountV2() {
  return post<SyncEwaybillAccountResult>("/v2/ewaybill_account/sync");
}

export interface EwaybillAccountMappingResult {
  ewaybillShippingAddressMapping: Map<number, EwaybillShippingAddress>;
  branchAccountMapping: Map<number, EwaybillBranchAccount>;
  subscriptionMapping: Map<number, EwaybillSubscription>;
  ewaybillAccountMapping: Map<number, EwaybillAccount>;
  ewaybillAccountExpiredResultMapping: Map<number, EwaybillAccountExpiredResult>;
  allShippingAddresses: EwaybillShippingAddress[];
}

interface EwaybillAccountExpiredResult {
  hasExpired: boolean;

  tokenExpiringTimeMillis: number;

  willExpired: boolean;
}

export function getEwaybillAccountMapping(
  items: EwaybillAccountListWithExpiredInfoItem[]
): EwaybillAccountMappingResult {
  const ewaybillShippingAddressMapping = new Map<number, EwaybillShippingAddress>();
  const branchAccountMapping = new Map<number, EwaybillBranchAccount>();
  const subscriptionMapping = new Map<number, EwaybillSubscription>();
  const ewaybillAccountMapping = new Map<number, EwaybillAccount>();
  const ewaybillAccountExpiredResultMapping = new Map<number, EwaybillAccountExpiredResult>();

  const allShippingAddresses: EwaybillShippingAddress[] = [];

  items?.forEach((item) => {
    const { hasExpired, tokenExpiringTimeMillis, willExpired } = item;

    ewaybillAccountExpiredResultMapping.set(item.ewaybillAccount.id, {
      hasExpired,
      tokenExpiringTimeMillis,
      willExpired,
    });

    item.ewaybillAccount.subscriptions?.forEach((subscription) => {
      subscription.branchAccounts?.forEach((branchAccount) => {
        branchAccount.shippingAddresses?.forEach((shippingAddress) => {
          ewaybillShippingAddressMapping.set(shippingAddress.id, shippingAddress);

          branchAccountMapping.set(shippingAddress.id, branchAccount);

          subscriptionMapping.set(shippingAddress.id, subscription);

          ewaybillAccountMapping.set(shippingAddress.id, item.ewaybillAccount);

          allShippingAddresses.push(shippingAddress);
        });
      });
    });
  });

  return {
    ewaybillShippingAddressMapping,
    branchAccountMapping,
    subscriptionMapping,
    ewaybillAccountMapping,
    ewaybillAccountExpiredResultMapping,
    allShippingAddresses,
  };
}

export function updateEwaybillAccountName(id: number, name: string) {
  return postJson(`/v2/ewaybill_account/update/${id}:name`, { name });
}

export function deleteEwaybillAccount(id: number) {
  return postJson(`/v2/ewaybill_account/delete/${id}`);
}
